import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { DecisionNode, EndStepComponent } from '../../../core/models/decision-tree.model';
import { AnfrageWizardStore, UploadedPhoto } from '../store/anfrage-wizard.store';
import { AnfrageService } from '../../../core/services/anfrage.service';
import { AuthService } from '../../../core/services/auth.service';
import { PlaceDetails } from '../../../core/services/places.service';
import { AddressAutocompleteComponent } from '../../../shared/components/address-autocomplete/address-autocomplete.component';
import { NodeRendererInputs } from '../renderers/node-renderer';
import {
  AnfrageAufzugStatus,
  AnfrageOertlicheBedingungen,
  AnfrageParken,
  AnfrageStockwerk,
} from '../../../core/models/anfrage.model';

type BesonderheitKey =
  | 'schmalerTreppenaufgang'
  | 'altbau'
  | 'denkmalschutz'
  | 'mehrfamilienhaus'
  | 'gewerbeobjekt'
  | 'tierhaltung';

interface PhotoConfig { maxFiles: number; helpText: string; }
interface NotesConfig { maxLength: number; placeholder: string; required: boolean; }

const DEFAULT_PHOTO: PhotoConfig = { maxFiles: 5, helpText: '' };
const DEFAULT_NOTES: NotesConfig = { maxLength: 500, placeholder: '', required: false };
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic'];
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

/**
 * Entdoppelt einen kombinierten Adress-String auf die vier sauberen Felder.
 * Beispiel: "Friedrich-Ebert-Str. 23a, 42103 Wuppertal"
 *   → { strasse: 'Friedrich-Ebert-Str.', hausnummer: '23a', plz: '42103', ort: 'Wuppertal' }
 * Nur best-effort: was nicht erkannt wird, bleibt leer und kann manuell ergänzt werden.
 */
export function parseCombinedAddress(full: string): {
  strasse: string; hausnummer: string; plz: string; ort: string;
} {
  let plz = '', ort = '', streetPart = full.trim();

  const plzMatch = streetPart.match(/\b(\d{5})\s+(.+)$/);
  if (plzMatch && plzMatch.index !== undefined) {
    plz = plzMatch[1];
    ort = plzMatch[2].trim();
    streetPart = streetPart.slice(0, plzMatch.index).replace(/[,\s]+$/, '').trim();
  }

  let strasse = streetPart, hausnummer = '';
  // Hausnummer am Ende: Ziffern + optional Buchstabe, ggf. "12-14" / "12/3".
  const numMatch = streetPart.match(/^(.*?[^\s,])[\s,]+(\d+\s*[a-zA-Z]?(?:\s*[-/]\s*\d+\s*[a-zA-Z]?)?)$/);
  if (numMatch) {
    strasse = numMatch[1].replace(/[,\s]+$/, '').trim();
    hausnummer = numMatch[2].replace(/\s+/g, '');
  }

  return { strasse, hausnummer, plz, ort };
}

/**
 * Renders the summary_and_extras (endStep) node:
 *   • live FE summary preview
 *   • notfall trust banner (when autoSetUrgency=notfall fired earlier)
 *   • photo upload (real backend call, drag&drop, thumbnails)
 *   • free-text notes
 *   • address confirmation (PLZ/Ort required, street optional)
 *   • submit → /anfrage/neu/auswahl (final submit lives in Prompt 3)
 */
@Component({
  selector: 'app-end-step-renderer',
  standalone: true,
  imports: [ReactiveFormsModule, AddressAutocompleteComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './end-step-renderer.component.html',
  styleUrl: './end-step-renderer.component.scss',
})
export class EndStepRendererComponent implements NodeRendererInputs, OnInit {
  private readonly fb = inject(FormBuilder);
  /** Public so the template can read `store.notes()` for the live counter. */
  readonly store = inject(AnfrageWizardStore);
  private readonly anfrage = inject(AnfrageService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  @Input({ required: true }) node!: DecisionNode;
  @Input({ required: true }) nodeKey!: string;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  /** Per-component config from spec.endStep.components (with sane defaults). */
  photoCfg: PhotoConfig = DEFAULT_PHOTO;
  notesCfg: NotesConfig = DEFAULT_NOTES;
  addressRequired = true;

  // Reactive form for the address confirmation block.
  // Strasse steht oben + ist Pflicht, weil das Autocomplete dort den Rest
  // (PLZ, Ort, Hausnummer) auto-fillt sobald jemand die volle Adresse tippt.
  readonly addressForm = this.fb.nonNullable.group({
    strasse:    ['', [Validators.required]],
    hausnummer: [''],
    plz:        ['', [Validators.required, Validators.pattern(/^\d{5}$/)]],
    ort:        ['', [Validators.required]],
  });

  uploadError: string | null = null;
  uploading = false;
  isDragging = false;

  // ── Computed UX state ──────────────────────────────────────────────────────
  readonly summary = this.store.summaryPreview;
  readonly photos = this.store.photos;
  readonly isNotfall = computed(() => this.store.effectiveUrgency() === 'notfall');

  /** Edge case: if any answer = "unbekannt", emphasise photo upload. */
  readonly photoEmphasised = computed(() => {
    const answers = this.store.answers();
    return Object.values(answers).some((v) => v === 'unbekannt');
  });

  /** Edge case: if any answer = "sonstiges", encourage the free-text note. */
  readonly notesEmphasised = computed(() => {
    const answers = this.store.answers();
    return Object.values(answers).some((v) => v === 'sonstiges');
  });

  ngOnInit(): void {
    this.applySpecConfig(this.node.components ?? []);
    this.hydrateFromStore();
    this.tryPrefillAddress();
  }

  // ── Spec config wiring ─────────────────────────────────────────────────────

  private applySpecConfig(components: EndStepComponent[]): void {
    for (const c of components) {
      if (c.type === 'photo_upload') {
        this.photoCfg = {
          maxFiles: c.maxFiles ?? DEFAULT_PHOTO.maxFiles,
          helpText: c.helpText ?? '',
        };
      } else if (c.type === 'free_text') {
        this.notesCfg = {
          maxLength: c.maxLength ?? DEFAULT_NOTES.maxLength,
          placeholder: c.placeholder ?? '',
          required: !!c.required,
        };
      } else if (c.type === 'address_confirm') {
        this.addressRequired = c.required ?? true;
      }
    }
  }

  private hydrateFromStore(): void {
    const a = this.store.address();
    let strasse = a.strasse ?? '';
    let hausnummer = a.hausnummer ?? '';
    let plz = a.plz ?? '';
    let ort = a.ort ?? '';

    // Entdoppeln: steckt die volle Adresse noch im Straße-Feld (Altbestand /
    // Vorbefüllung), sauber auf die vier Felder aufteilen — ohne bereits
    // separat gesetzte Werte zu überschreiben.
    if (strasse && /\b\d{5}\b/.test(strasse) && (!plz || !ort)) {
      const parsed = parseCombinedAddress(strasse);
      strasse = parsed.strasse || strasse;
      hausnummer = hausnummer || parsed.hausnummer;
      plz = plz || parsed.plz;
      ort = ort || parsed.ort;
    }

    this.addressForm.patchValue({ strasse, hausnummer, plz, ort });
    // Entdoppelte Werte zurück in den Store, damit sie persistiert bleiben.
    this.onAddressChange();
  }

  /** Pull a default address from the logged-in user (we have no per-user profile yet,
   *  but at least vorname/nachname are available). PLZ/Ort come later. */
  private tryPrefillAddress(): void {
    this.auth.currentUser$.pipe(take(1)).subscribe((user) => {
      if (!user) return;
      // No structured address on the basic /auth/me yet — leave the fields empty.
      // (Server stays the source of truth at submit; future enhancement: GET /auth/me/profile.)
    });
  }

  // ── Notes ──────────────────────────────────────────────────────────────────

  onNotesChange(value: string): void { this.store.setNotes(value); }

  // ── Örtliche Gegebenheiten (Konzept § 8) ──────────────────────────────────

  readonly stockwerkOptions: ReadonlyArray<{ value: AnfrageStockwerk; label: string }> = [
    { value: 'eg',     label: 'EG' },
    { value: '1og',    label: '1. OG' },
    { value: '2og',    label: '2. OG' },
    { value: '3plus',  label: '3.+ OG' },
    { value: 'keller', label: 'Keller' },
  ];

  readonly aufzugOptions: ReadonlyArray<{ value: AnfrageAufzugStatus; label: string }> = [
    { value: 'ja',     label: 'Ja' },
    { value: 'nein',   label: 'Nein' },
    { value: 'defekt', label: 'Defekt' },
  ];

  readonly parkenOptions: ReadonlyArray<{ value: AnfrageParken; label: string }> = [
    { value: 'eigene_einfahrt',  label: 'Eigene Einfahrt / direkt vor dem Haus' },
    { value: 'strassenparken',   label: 'Straßenparken (kostenlos)' },
    { value: 'bewohnerparken',   label: 'Bewohnerparken / Parkschein' },
    { value: 'schwierig',        label: 'Schwierig / nicht vorhanden' },
  ];

  readonly besonderheitenOptions: ReadonlyArray<{ key: BesonderheitKey; label: string }> = [
    { key: 'schmalerTreppenaufgang', label: 'Schmaler Treppenaufgang' },
    { key: 'altbau',                 label: 'Altbau (vor 1950)' },
    { key: 'denkmalschutz',          label: 'Denkmalschutz' },
    { key: 'mehrfamilienhaus',       label: 'Mehrfamilienhaus' },
    { key: 'gewerbeobjekt',          label: 'Gewerbeobjekt' },
    { key: 'tierhaltung',            label: 'Tierhaltung (Hund/Katze)' },
  ];

  setStockwerk(value: AnfrageStockwerk): void { this.store.patchOertliche({ stockwerk: value }); }
  setAufzug(value: AnfrageAufzugStatus): void { this.store.patchOertliche({ aufzugStatus: value }); }
  setParken(value: AnfrageParken): void { this.store.patchOertliche({ parkmoeglichkeit: value }); }

  isBesonderheitChecked(key: BesonderheitKey): boolean {
    return !!(this.store.oertliche() as AnfrageOertlicheBedingungen)[key];
  }

  toggleBesonderheit(key: BesonderheitKey): void {
    const cur = this.store.oertliche();
    this.store.patchOertliche({ [key]: !cur[key] } as Partial<AnfrageOertlicheBedingungen>);
  }

  // ── Address ────────────────────────────────────────────────────────────────

  onAddressChange(): void {
    const v = this.addressForm.getRawValue();
    this.store.setAddress({
      plz: v.plz.trim(),
      ort: v.ort.trim(),
      strasse: v.strasse.trim() || undefined,
      hausnummer: v.hausnummer.trim() || undefined,
    });
  }

  /**
   * Wird vom <app-address-autocomplete> bei jeder Eingabe gefeuert. Hält
   * strasse mit dem Form-Control synchron — emitEvent so dass der Store
   * via onAddressChange im Template ausgelöst wird.
   */
  onStrasseInput(value: string): void {
    this.addressForm.controls.strasse.setValue(value);
    this.addressForm.controls.strasse.markAsDirty();
    this.onAddressChange();
  }

  /**
   * User hat eine Suggestion aus dem Dropdown gewählt — strasse + hausnummer
   * + plz + ort werden befüllt. Mapbox liefert "stadt" — wir mappen auf
   * unser "ort"-Feld.
   */
  onAddressPicked(details: PlaceDetails): void {
    const c = details.components;
    this.addressForm.patchValue({
      strasse: c.strasse ?? this.addressForm.controls.strasse.value,
      hausnummer: c.hausnummer ?? this.addressForm.controls.hausnummer.value,
      plz: c.plz ?? this.addressForm.controls.plz.value,
      ort: c.stadt ?? this.addressForm.controls.ort.value,
    });
    this.addressForm.markAsDirty();
    this.onAddressChange();
  }

  // ── Photo upload ───────────────────────────────────────────────────────────

  triggerFilePicker(): void { this.fileInput?.nativeElement.click(); }

  onFileInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) this.uploadFiles(Array.from(input.files));
    input.value = '';
  }

  onDragOver(event: DragEvent): void { event.preventDefault(); this.isDragging = true; }
  onDragLeave(event: DragEvent): void { event.preventDefault(); this.isDragging = false; }
  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    if (event.dataTransfer?.files) this.uploadFiles(Array.from(event.dataTransfer.files));
  }

  removePhoto(p: UploadedPhoto): void {
    // TODO: delete on server (no DELETE endpoint yet — Prompt 1 left photos
    // attachable via SubmitAnfrage and a Background-cleanup TODO. Removing
    // locally is enough for now — orphaned uploads expire server-side.)
    this.store.removePhoto(p.clientId);
  }

  private uploadFiles(files: File[]): void {
    this.uploadError = null;
    const remaining = this.photoCfg.maxFiles - this.photos().length;
    const eligible = files.slice(0, Math.max(remaining, 0));

    for (const file of eligible) {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
        this.uploadError = `Dateityp '${file.type || 'unbekannt'}' nicht erlaubt.`;
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        this.uploadError = `Datei "${file.name}" überschreitet 10 MB.`;
        continue;
      }
      this.uploadOne(file);
    }
  }

  private uploadOne(file: File): void {
    const clientId = this.makeClientId();
    this.uploading = true;
    this.anfrage.uploadPhoto(file).subscribe({
      next: (res) => {
        this.store.addPhoto({
          url: res.url,
          contentType: file.type,
          groesse: file.size,
          clientId,
        });
        this.uploading = false;
      },
      error: (err) => {
        this.uploadError =
          err?.status === 401
            ? 'Bitte logge dich ein, um Fotos hochzuladen.'
            : 'Upload fehlgeschlagen — bitte erneut versuchen.';
        this.uploading = false;
      },
    });
  }

  private makeClientId(): string {
    return `cl-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  // ── Submit ────────────────────────────────────────────────────────────────

  /**
   * Erstes noch offenes Pflichtfeld (`*`) in Lese-Reihenfolge — oder null, wenn
   * alles gesetzt ist. Treibt sowohl den „Noch: …"-Hinweis als auch canSubmit.
   * Reihenfolge = Reihenfolge im Formular (Vor-Ort-Hinweise zuerst, dann Adresse).
   */
  get missingLabel(): string | null {
    const o = this.store.oertliche();
    if (!o.stockwerk) return 'Stockwerk wählen';
    if (!o.aufzugStatus) return 'Aufzug wählen';
    if (!o.parkmoeglichkeit) return 'Parkmöglichkeit wählen';
    if (this.addressRequired) {
      const v = this.addressForm.getRawValue();
      if (!v.strasse.trim()) return 'Straße angeben';
      if (!/^\d{5}$/.test(v.plz.trim())) return 'PLZ angeben';
      if (!v.ort.trim()) return 'Ort angeben';
    }
    return null;
  }

  get canSubmit(): boolean {
    return this.missingLabel === null;
  }

  submit(): void {
    if (!this.canSubmit) {
      this.addressForm.markAllAsTouched();
      return;
    }
    this.onAddressChange();
    // Final POST /api/anfragen happens in Prompt 3 — for now just hand off
    // to the placeholder route. The store snapshot survives the navigation.
    void this.router.navigate(['/anfrage/neu/auswahl']);
  }
}
